import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function ProgrammesTransport() {
    const [programmes, setProgrammes] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [nonLus, setNonLus] = useState(0);
    const [loading, setLoading] = useState(true);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const fetchProgrammes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/get_programmes_transport.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProgrammes((data.programmes || []).map(programme => ({
                    ...programme,
                    bus_photo: config.normalizeBusPhoto(programme.bus_photo)
                }))); 
            }
        } catch (error) {
            toast.error('Erreur chargement programmes');
        }
    };

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/get_notifications_transport.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setNotifications(data.notifications);
                setNonLus(data.non_lus);
            }
        } catch (error) {
            console.error('Erreur notifications:', error);
        }
    };

    const marquerLue = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_BASE}/financier/marquer_notification_lue.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id_notif: id })
            });
            fetchNotifications();
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    useEffect(() => {
        fetchProgrammes();
        fetchNotifications();
        const interval = setInterval(() => {
            fetchNotifications();
        }, 30000);
        setLoading(false);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-5xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white shadow-xl">
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-2xl font-bold">🚌 Programmes de transport</h1>
                            <p className="mt-2 opacity-90">Consultez les programmes de bus du jour</p>
                        </div>
                        {nonLus > 0 && (
                            <div className="relative">
                                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-yellow-500 animate-pulse">
                                    <span className="text-xl font-bold">{nonLus}</span>
                                </div>
                                <div className="absolute -right-2 -top-2">
                                    <span className="flex h-3 w-3">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500"></span>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Notifications */}
                {notifications.length > 0 && (
                    <div className="mb-6 rounded-2xl bg-white p-5 shadow-lg">
                        <h2 className="font-semibold text-slate-800 mb-3">🔔 Nouvelles notifications</h2>
                        <div className="space-y-2">
                            {notifications.map(n => (
                                <div key={n.id_notif} className="flex items-center justify-between p-3 bg-blue-50 rounded-xl">
                                    <p className="text-sm">{n.message}</p>
                                    <button onClick={() => marquerLue(n.id_notif)} className="text-blue-600 text-sm hover:text-blue-800">✓ Marquer lue</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Programmes du jour */}
                {loading ? (
                    <div className="flex justify-center py-12"><div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div></div>
                ) : programmes.length === 0 ? (
                    <div className="rounded-2xl bg-white p-12 text-center">
                        <div className="text-5xl mb-3">🚌</div>
                        <p className="text-slate-500">Aucun programme de transport pour aujourd'hui</p>
                    </div>
                ) : (
                    <div className="grid gap-5">
                        {programmes.map(p => (
                            <div key={p.id_programme} className="rounded-2xl bg-white p-5 shadow-lg">
                                <div className="flex flex-col md:flex-row gap-5">
                                    <div className="flex-shrink-0">
                                        {p.bus_photo ? (
                                            <img src={p.bus_photo} alt={p.bus_nom} className="w-32 h-24 object-cover rounded-xl" />
                                        ) : (
                                            <div className="w-32 h-24 bg-slate-100 rounded-xl flex items-center justify-center text-3xl">🚌</div>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex justify-between items-start">
                                            <h3 className="text-xl font-bold text-slate-800">{p.bus_nom}</h3>
                                            <span className={`text-xs px-2 py-1 rounded-full ${p.besoin_huile ? 'bg-orange-100 text-orange-600' : 'bg-green-100 text-green-600'}`}>
                                                {p.besoin_huile ? '🛢️ Besoin d\'huile' : '✅ Huile OK'}
                                            </span>
                                        </div>
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div>
                                                <p className="text-slate-500">👨‍✈️ Chauffeur</p>
                                                <p className="font-medium">{p.chauffeur_prenom} {p.chauffeur_nom}</p>
                                            </div>
                                            <div>
                                                <p className="text-slate-500">📞 Téléphone</p>
                                                <p className="font-medium">{p.chauffeur_telephone || 'Non renseigné'}</p>
                                            </div>
                                        </div>
                                        <div className="mt-3">
                                            <p className="text-slate-500">📝 Description du voyage</p>
                                            <p className="text-sm mt-1 bg-slate-50 p-3 rounded-xl">{p.description}</p>
                                        </div>
                                        <div className="mt-3 text-xs text-slate-400">
                                            Programmé le {new Date(p.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}